import React, { useEffect, useState, useCallback } from "react";

// Super-admin Billing & Invoicing. Overview of every organization (prepaid balance +
// month-to-date usage + monthly statements) and every client (invoices from approved
// quotes, paid via Stripe). Calls /api/super-admin/invoices/* + /quotes (Bearer idToken).

const box = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 16 };
const btn = { padding: "8px 14px", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 };
const primary = { ...btn, background: "#2563eb", color: "#fff" };
const subtle = { ...btn, background: "#e2e8f0", color: "#0f172a" };
const good = { ...btn, background: "#16a34a", color: "#fff" };
const th = { textAlign: "left", padding: "8px 10px", borderBottom: "2px solid #273d3b", background: "#273d3b", color: "#fff" };
const td = { padding: "8px 10px", borderBottom: "1px solid #e2e8f0" };
const tab = (active) => ({ ...btn, background: active ? "#273d3b" : "#e2e8f0", color: active ? "#fff" : "#0f172a" });
const money = (n, cur = "USD") => `${cur} ${Number(n || 0).toFixed(2)}`;

function authHeaders(json = true) {
    const h = { Authorization: `Bearer ${localStorage.getItem("idToken")}` };
    if (json) h["Content-Type"] = "application/json";
    return h;
}
const api = (url, opts = {}) => fetch(url, { headers: authHeaders(), credentials: "include", ...opts });

const STATUS_COLOR = {
    paid: "#16a34a", sent: "#2563eb", issued: "#64748b", partially_paid: "#d97706", void: "#94a3b8", draft: "#94a3b8",
};
function StatusPill({ status }) {
    return <span style={{ background: (STATUS_COLOR[status] || "#64748b") + "22", color: STATUS_COLOR[status] || "#64748b", padding: "2px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700 }}>{status}</span>;
}

export default function BillingPage() {
    const [tab_, setTab] = useState("organizations");
    const [overview, setOverview] = useState({ organizations: [], clients: [], period: "" });
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState("");
    const [flash, setFlash] = useState(null);
    const [orgDetail, setOrgDetail] = useState(null);   // { org, summary }
    const [clientDetail, setClientDetail] = useState(null); // { client, invoices, quotes }
    const [busy, setBusy] = useState(false);

    const loadOverview = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api("/api/super-admin/invoices/overview");
            const data = await res.json();
            setOverview(data);
        } catch (e) { setMessage("Failed to load billing overview."); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { loadOverview(); }, [loadOverview]);

    // ---- Org drill-down ----
    const openOrg = async (org) => {
        setFlash(null);
        const res = await api(`/api/super-admin/invoices/org/${org.id}/summary`);
        const summary = await res.json();
        setOrgDetail({ org, summary });
    };
    const refreshOrg = async () => { if (orgDetail) await openOrg(orgDetail.org); };

    const generateStatement = async (orgId, period, force) => {
        setBusy(true); setFlash(null);
        try {
            const res = await api(`/api/super-admin/invoices/org/${orgId}/generate`, { method: "POST", body: JSON.stringify({ period: period || undefined, force: !!force }) });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Failed");
            setFlash({ kind: "success", text: data.skipped ? `Statement already exists for ${data.period}.` : `Statement generated for ${data.period}.` });
            await refreshOrg(); loadOverview();
        } catch (e) { setFlash({ kind: "error", text: e.message }); } finally { setBusy(false); }
    };

    const sendInvoice = async (id) => {
        setBusy(true); setFlash(null);
        try {
            const res = await api(`/api/super-admin/invoices/${id}/send`, { method: "POST", body: "{}" });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Failed");
            setFlash({ kind: "success", text: data.sent ? `Emailed to ${data.to}.` : "Nothing sent (no billing email)." });
            await refreshOrg();
        } catch (e) { setFlash({ kind: "error", text: e.message }); } finally { setBusy(false); }
    };

    const downloadPdf = async (id) => {
        try {
            const res = await api(`/api/super-admin/invoices/${id}/pdf`);
            const data = await res.json();
            if (data.url) window.open(data.url, "_blank");
            else setFlash({ kind: "error", text: data.error || "No PDF" });
        } catch (e) { setFlash({ kind: "error", text: e.message }); }
    };

    // ---- Client drill-down ----
    const openClient = async (client) => {
        setFlash(null);
        const [invRes, qRes] = await Promise.all([
            api(`/api/super-admin/invoices/client/${client.id}`),
            api(`/api/super-admin/quotes?client_id=${client.id}`),
        ]);
        const inv = await invRes.json();
        const quotes = (await qRes.json()).quotes || [];
        setClientDetail({ client, invoices: inv.invoices || [], quotes });
    };
    const refreshClient = async () => { if (clientDetail) await openClient(clientDetail.client); };

    const createFromQuote = async (clientId, quoteId) => {
        setBusy(true); setFlash(null);
        try {
            const res = await api(`/api/super-admin/invoices/client/${clientId}/from-quote/${quoteId}`, { method: "POST", body: "{}" });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Failed");
            setFlash({ kind: "success", text: data.skipped ? "Invoice already exists for this quote." : "Invoice created." });
            await refreshClient(); loadOverview();
        } catch (e) { setFlash({ kind: "error", text: e.message }); } finally { setBusy(false); }
    };

    const paymentLink = async (id) => {
        setBusy(true); setFlash(null);
        try {
            const res = await api(`/api/super-admin/invoices/${id}/payment-link`, { method: "POST", body: "{}" });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Failed");
            setFlash({ kind: "success", text: "Payment link created." });
            if (data.url) window.open(data.url, "_blank");
            await refreshClient();
        } catch (e) { setFlash({ kind: "error", text: e.message }); } finally { setBusy(false); }
    };

    const markPaid = async (id) => {
        if (!window.confirm("Mark this invoice as paid (offline)?")) return;
        setBusy(true); setFlash(null);
        try {
            const res = await api(`/api/super-admin/invoices/${id}/mark-paid`, { method: "POST", body: "{}" });
            if (!res.ok) throw new Error((await res.json()).error || "Failed");
            setFlash({ kind: "success", text: "Marked paid." });
            await refreshClient(); loadOverview();
        } catch (e) { setFlash({ kind: "error", text: e.message }); } finally { setBusy(false); }
    };

    const voidInvoice = async (id) => {
        if (!window.confirm("Void this invoice?")) return;
        setBusy(true); setFlash(null);
        try {
            const res = await api(`/api/super-admin/invoices/${id}/void`, { method: "POST", body: "{}" });
            if (!res.ok) throw new Error((await res.json()).error || "Failed");
            setFlash({ kind: "success", text: "Voided." });
            await refreshClient(); await refreshOrg(); loadOverview();
        } catch (e) { setFlash({ kind: "error", text: e.message }); } finally { setBusy(false); }
    };

    return (
        <div style={{ padding: 20 }}>
            <h2 style={{ marginTop: 0 }}>Billing &amp; Invoicing</h2>
            <p style={{ color: "#64748b", marginTop: -6 }}>Prior full month for statements: <strong>{overview.period || "—"}</strong></p>
            {message && <div style={{ ...box, borderColor: "#fca5a5", background: "#fef2f2", color: "#b91c1c" }}>{message}</div>}

            <div style={{ display: "flex", gap: 10, margin: "12px 0" }}>
                <button style={tab(tab_ === "organizations")} onClick={() => setTab("organizations")}>Organizations</button>
                <button style={tab(tab_ === "clients")} onClick={() => setTab("clients")}>Clients</button>
            </div>

            {loading ? <p>Loading…</p> : tab_ === "organizations" ? (
                <div style={box}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead><tr>
                            <th style={th}>Organization</th><th style={th}>Prepaid balance</th>
                            <th style={th}>Charges (this month)</th><th style={th}>Last statement</th><th style={th}>Actions</th>
                        </tr></thead>
                        <tbody>
                            {overview.organizations.map((o) => (
                                <tr key={o.id}>
                                    <td style={td}>{o.display_name || o.organization_name}<div style={{ color: "#94a3b8", fontSize: 12 }}>{o.billing_contact_email || "no billing email"}</div></td>
                                    <td style={{ ...td, color: o.balance < 0 ? "#b91c1c" : "#0f172a", fontWeight: 600 }}>{money(o.balance)}</td>
                                    <td style={td}>{money(o.month_to_date_charges)}</td>
                                    <td style={td}>{o.last_statement ? <><StatusPill status={o.last_statement.status} /> <span style={{ color: "#64748b", fontSize: 12 }}>{String(o.last_statement.period_start).slice(0, 7)}</span></> : <span style={{ color: "#94a3b8" }}>none</span>}</td>
                                    <td style={td}><button style={subtle} onClick={() => openOrg(o)}>View</button></td>
                                </tr>
                            ))}
                            {!overview.organizations.length && <tr><td style={td} colSpan={5}>No organizations.</td></tr>}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div style={box}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead><tr>
                            <th style={th}>Client</th><th style={th}>Type</th><th style={th}>Invoices</th><th style={th}>Outstanding</th><th style={th}>Actions</th>
                        </tr></thead>
                        <tbody>
                            {overview.clients.map((c) => (
                                <tr key={c.id}>
                                    <td style={td}>{c.client_name}</td>
                                    <td style={td}>{c.client_type}</td>
                                    <td style={td}>{c.invoice_count}</td>
                                    <td style={{ ...td, fontWeight: 600, color: Number(c.outstanding) > 0 ? "#d97706" : "#0f172a" }}>{money(c.outstanding)}</td>
                                    <td style={td}><button style={subtle} onClick={() => openClient(c)}>View</button></td>
                                </tr>
                            ))}
                            {!overview.clients.length && <tr><td style={td} colSpan={5}>No clients.</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}

            {orgDetail && <OrgModal detail={orgDetail} flash={flash} busy={busy} onClose={() => { setOrgDetail(null); setFlash(null); }}
                onGenerate={generateStatement} onSend={sendInvoice} onDownload={downloadPdf} onVoid={voidInvoice} />}
            {clientDetail && <ClientModal detail={clientDetail} flash={flash} busy={busy} onClose={() => { setClientDetail(null); setFlash(null); }}
                onFromQuote={createFromQuote} onSend={sendInvoice} onDownload={downloadPdf} onPaymentLink={paymentLink} onMarkPaid={markPaid} onVoid={voidInvoice} />}
        </div>
    );
}

function Modal({ children, onClose, title }) {
    return (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
            <div style={{ width: "90vw", height: "90vh", background: "#fff", borderRadius: 12, padding: 24, overflow: "auto", position: "relative" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <h3 style={{ margin: 0 }}>{title}</h3>
                    <button style={{ ...btn, background: "#e2e8f0" }} onClick={onClose}>Close</button>
                </div>
                {children}
            </div>
        </div>
    );
}
function Flash({ flash }) {
    if (!flash) return null;
    const ok = flash.kind === "success";
    return <div style={{ margin: "10px 0", padding: "10px 14px", borderRadius: 8, background: ok ? "#f0fdf4" : "#fef2f2", color: ok ? "#166534" : "#b91c1c", border: `1px solid ${ok ? "#bbf7d0" : "#fca5a5"}` }}>{flash.text}</div>;
}

function OrgModal({ detail, flash, busy, onClose, onGenerate, onSend, onDownload, onVoid }) {
    const { org, summary } = detail;
    const [period, setPeriod] = useState("");
    return (
        <Modal title={`Billing — ${org.display_name || org.organization_name}`} onClose={onClose}>
            <Flash flash={flash} />
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                <div style={{ ...box, flex: "1 1 320px" }}>
                    <h4 style={{ marginTop: 0 }}>Prepaid balance</h4>
                    <div style={{ fontSize: 28, fontWeight: 700, color: summary.balance < 0 ? "#b91c1c" : "#16a34a" }}>{money(summary.balance)}</div>
                    <h4>Charges this month by type</h4>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <tbody>
                            {(summary.month_to_date || []).map((r) => (
                                <tr key={r.type}><td style={td}>{r.type} <span style={{ color: "#94a3b8" }}>({r.count})</span></td><td style={{ ...td, textAlign: "right" }}>{money(r.amount)}</td></tr>
                            ))}
                            {!(summary.month_to_date || []).length && <tr><td style={td} colSpan={2}>No charges this month.</td></tr>}
                        </tbody>
                    </table>
                </div>
                <div style={{ ...box, flex: "1 1 320px" }}>
                    <h4 style={{ marginTop: 0 }}>Generate monthly statement</h4>
                    <p style={{ color: "#64748b", fontSize: 13 }}>Leave the period blank to generate the prior full month. Format YYYY-MM.</p>
                    <input placeholder="YYYY-MM (optional)" value={period} onChange={(e) => setPeriod(e.target.value)} style={{ padding: 8, border: "1px solid #cbd5e1", borderRadius: 6, boxSizing: "border-box", width: "100%", marginBottom: 8 }} />
                    <div style={{ display: "flex", gap: 8 }}>
                        <button style={primary} disabled={busy} onClick={() => onGenerate(org.id, period, false)}>Generate</button>
                        <button style={subtle} disabled={busy} onClick={() => onGenerate(org.id, period, true)}>Regenerate (force)</button>
                    </div>
                </div>
            </div>

            <h4 style={{ marginTop: 20 }}>Invoice history</h4>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>
                    <th style={th}>Number</th><th style={th}>Kind</th><th style={th}>Period</th><th style={th}>Total</th><th style={th}>Status</th><th style={th}>Actions</th>
                </tr></thead>
                <tbody>
                    {(summary.invoices || []).map((inv) => (
                        <tr key={inv.id}>
                            <td style={td}>{inv.invoice_number}</td>
                            <td style={td}>{inv.kind.replace(/_/g, " ")}</td>
                            <td style={td}>{inv.period_start ? String(inv.period_start).slice(0, 7) : "—"}</td>
                            <td style={td}>{money(inv.total)}</td>
                            <td style={td}><StatusPill status={inv.status} /></td>
                            <td style={{ ...td, whiteSpace: "nowrap" }}>
                                <button style={{ ...subtle, marginRight: 6 }} onClick={() => onDownload(inv.id)}>PDF</button>
                                <button style={{ ...subtle, marginRight: 6 }} disabled={busy} onClick={() => onSend(inv.id)}>Email</button>
                                {inv.status !== "void" && <button style={{ ...subtle, color: "#b91c1c" }} disabled={busy} onClick={() => onVoid(inv.id)}>Void</button>}
                            </td>
                        </tr>
                    ))}
                    {!(summary.invoices || []).length && <tr><td style={td} colSpan={6}>No invoices yet.</td></tr>}
                </tbody>
            </table>
        </Modal>
    );
}

function ClientModal({ detail, flash, busy, onClose, onFromQuote, onSend, onDownload, onPaymentLink, onMarkPaid, onVoid }) {
    const { client, invoices, quotes } = detail;
    const approved = (quotes || []).filter((q) => q.current && q.current.status === "approved");
    return (
        <Modal title={`Invoices — ${client.client_name}`} onClose={onClose}>
            <Flash flash={flash} />
            <div style={{ ...box, marginBottom: 16 }}>
                <h4 style={{ marginTop: 0 }}>Create invoice from an approved quote</h4>
                {approved.length ? (
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <tbody>
                            {approved.map((q) => (
                                <tr key={q.id}>
                                    <td style={td}>{q.quote_number || `Quote #${q.id}`} {q.proposal_name ? <span style={{ color: "#94a3b8" }}>· {q.proposal_name}</span> : null}</td>
                                    <td style={{ ...td, textAlign: "right" }}>{money(q.current.total, q.current.currency)}</td>
                                    <td style={{ ...td, textAlign: "right" }}><button style={good} disabled={busy} onClick={() => onFromQuote(client.id, q.id)}>Create invoice</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : <p style={{ color: "#64748b" }}>No approved quotes for this client yet.</p>}
            </div>

            <h4>Invoices</h4>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>
                    <th style={th}>Number</th><th style={th}>Total</th><th style={th}>Paid</th><th style={th}>Due</th><th style={th}>Status</th><th style={th}>Actions</th>
                </tr></thead>
                <tbody>
                    {(invoices || []).map((inv) => (
                        <tr key={inv.id}>
                            <td style={td}>{inv.invoice_number}</td>
                            <td style={td}>{money(inv.total, inv.currency)}</td>
                            <td style={td}>{money(inv.amount_paid, inv.currency)}</td>
                            <td style={td}>{inv.due_date ? String(inv.due_date).slice(0, 10) : "—"}</td>
                            <td style={td}><StatusPill status={inv.status} /></td>
                            <td style={{ ...td, whiteSpace: "nowrap" }}>
                                <button style={{ ...subtle, marginRight: 6 }} onClick={() => onDownload(inv.id)}>PDF</button>
                                {inv.status !== "paid" && inv.status !== "void" && <>
                                    <button style={{ ...subtle, marginRight: 6 }} disabled={busy} onClick={() => onPaymentLink(inv.id)}>Pay link</button>
                                    <button style={{ ...subtle, marginRight: 6 }} disabled={busy} onClick={() => onSend(inv.id)}>Email</button>
                                    <button style={{ ...good, marginRight: 6 }} disabled={busy} onClick={() => onMarkPaid(inv.id)}>Mark paid</button>
                                    <button style={{ ...subtle, color: "#b91c1c" }} disabled={busy} onClick={() => onVoid(inv.id)}>Void</button>
                                </>}
                            </td>
                        </tr>
                    ))}
                    {!(invoices || []).length && <tr><td style={td} colSpan={6}>No invoices yet.</td></tr>}
                </tbody>
            </table>
        </Modal>
    );
}
